import { z, ZodType } from 'zod'

import { AssignLecResponsableToCentersInput } from '../../../api/centers/assign-lec-responsable'
import { SchoolLevelEnum } from '../../../lib/types/enums/common/school-level.enum'
import { StaffQualificationEnum } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  AssignJourneyToCenterInput,
  CenterInput
} from '../../../lib/types/models/center/center-input'
import { CenterToJourneyInput } from '../../../lib/types/models/center-to-journey/center-to-journey-input'
import { StaffRequestInput } from '../../../lib/types/models/staff-request/staff-request-input'
import { TripLight } from '../../../lib/types/models/trip/trip'
import { TripPlaceToCenterRequestInput } from '../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request-input'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

// Passthrough is used to allow additional fields that are not defined in the schema
export const CENTER_TO_JOURNEY_SCHEMA: ZodType<
  Pick<CenterToJourneyInput, 'maxNumberOfStudentPlaces'>
> = z
  .object({ maxNumberOfStudentPlaces: createNumberSchema('Nombre max de jeunes') })
  .passthrough()

export const STAFF_REQUEST_SCHEMA: ZodType<StaffRequestInput> = z.object({
  qualification: createEnumSchema({
    enumType: StaffQualificationEnum,
    fieldName: 'Qualification'
  }).optional(),
  numberOfStaff: createNumberSchema('Nombre'),
  isSpecialRequest: z.boolean().optional()
})

const CENTER_TRIP_SCHEMA: ZodType<Pick<TripLight, 'id'>> = z
  .object({ id: createNumberSchema('ID') })
  .passthrough() // <-- allows other fields to be present

type CenterToTrip = Omit<
  Pick<
    TripPlaceToCenterRequestInput,
    'numberOfStaffPlacesRequested' | 'numberOfStudentsPlacesRequested' | 'disableRestriction'
  >,
  'trip'
> & {
  trip: Pick<TripLight, 'id'>
}

export const CENTER_TO_TRIP_SCHEMA: ZodType<CenterToTrip> = z.object({
  trip: CENTER_TRIP_SCHEMA,
  numberOfStudentsPlacesRequested: createNumberSchema('Nombre max jeunes'),
  numberOfStaffPlacesRequested: createNumberSchema('Nombre encadrants', { optional: true }),
  disableRestriction: z.boolean().optional()
})

export const ASSIGN_JOURNEY_TO_CENTER_SCHEMA: ZodType<AssignJourneyToCenterInput> = z
  .object({
    centerToJourneys: z.array(CENTER_TO_JOURNEY_SCHEMA) as unknown as ZodType<
      CenterToJourneyInput[]
    >,
    staffRequests: z.array(STAFF_REQUEST_SCHEMA),
    tripPlacesRequests: z.array(CENTER_TO_TRIP_SCHEMA) as unknown as ZodType<
      TripPlaceToCenterRequestInput[]
    >
  })
  .passthrough()

export const CENTER_SCHEMA: ZodType<CenterInput> = z.object({
  name: createStringSchema('NOM du centre'),
  lecResponsableId: createNumberSchema('Responsable secteur', { nullish: true }),
  olId: createNumberSchema('Prestataire étranger', { nullish: true }),
  journeyPlaceId: createNumberSchema('Lieu de séjour', { nullish: true }),
  olJourneyPlaceId: createNumberSchema('Adresse du centre', { nullish: true }),
  minAge: createNumberSchema('Age minimum', { nullish: true }),
  maxAge: createNumberSchema('Age maximum', { nullish: true }),
  retainerAmount: createNumberSchema("Montant de l'acompte", { nullish: true }),
  minSchoolLevel: createEnumSchema({
    enumType: SchoolLevelEnum,
    fieldName: 'Classe min',
    nullish: true
  }),
  maxSchoolLevel: createEnumSchema({
    enumType: SchoolLevelEnum,
    fieldName: 'Classe max',
    nullish: true
  }),
  assignJourneyToCenter: ASSIGN_JOURNEY_TO_CENTER_SCHEMA
})

export const REQUEST_STAFF_SCHEMA: ZodType<StaffRequestInput> = z
  .object({
    qualification: createEnumSchema({
      enumType: StaffQualificationEnum,
      fieldName: 'Qualification'
    }),
    isSpecialRequest: z.boolean().optional(),
    numberOfStaff: createNumberSchema('Nombre', { isPositive: true }),
    formulas: createNumberArraySchema('formulas').optional(),
    countryId: createNumberSchema('countryId', { nullish: true }),
    startDate: createStringSchema('Date de début'),
    endDate: createStringSchema('Date de fin'),
    tripId: createNumberSchema('tripId', { nullish: true }),
    radId: createNumberSchema('radId', { nullish: true })
  })
  .superRefine((arg, ctx) => {
    // check if the departure date is before the arrival date
    if (arg.startDate && arg.endDate && arg.startDate > arg.endDate) {
      ctx.addIssue({
        path: ['endDate'],
        code: z.ZodIssueCode.custom,
        message: 'La date de fin doit être après la date de début'
      })
    }
  })

export const ASSIGN_LEC_RESPONSABLE_SCHEMA: ZodType<AssignLecResponsableToCentersInput> = z.object(
  { lecResponsableId: createNumberSchema('Responsable secteur') }
)
